type GraphQLRequestOptions = {
  query: string
  variables?: Record<string, any>
  method?: 'POST' | 'GET'
  headers?: Record<string, string>
}

export const AUTH_EXPIRED_EVENT = 'auth:expired'

const AUTH_ERROR_PATTERNS = [
  'unauthorized',
  'not authorized',
  'token has expired',
  'token is expired',
  'expired token',
  'invalid token',
  'token is invalid',
  'the incoming token',
  'missingauthenticationtoken'
]

const isAuthError = (status: number, message: string | undefined): boolean => {
  if (status === 401 || status === 403) return true
  if (!message) return false
  const normalized = message.toLowerCase()
  return AUTH_ERROR_PATTERNS.some((pattern) => normalized.includes(pattern))
}

const dispatchAuthExpired = (reason: string) => {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT, { detail: { reason } }))
}

class GraphQLClient {
  private url: string
  private defaultHeaders: Record<string, string>

  constructor({ url, idToken }: { url: string; idToken?: string }) {
    this.url = url
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      ...(idToken && { Authorization: `Bearer ${idToken}` })
    }
  }

  async request<T>(
    { query, variables, method = 'POST', headers }: GraphQLRequestOptions,
    retryCount = 3,
    retryDelay = 500 // milliseconds
  ): Promise<T> {
    let lastError: unknown = null

    for (let attempt = 0; attempt <= retryCount; attempt++) {
      try {
        const response = await fetch(this.url, {
          method,
          headers: {
            ...this.defaultHeaders,
            ...headers
          },
          body: JSON.stringify({ query, variables })
        })

        const result = await response.json()

        if (!response.ok || (Array.isArray(result.errors) && result.errors.length > 0)) {
          const errorMessage = result.errors?.[0]?.message || 'GraphQL request failed'

          // Detect expired / invalid session and notify the app to log out.
          // Do not retry auth errors — they won't recover.
          if (isAuthError(response.status, errorMessage)) {
            dispatchAuthExpired(errorMessage)
            throw new Error(errorMessage)
          }

          throw new Error(errorMessage)
        }

        return result.data
      } catch (error) {
        lastError = error
        const message = error instanceof Error ? error.message : ''
        const isAuth = isAuthError(0, message)

        // Auth failures are terminal — bail out of the retry loop immediately.
        if (isAuth) {
          dispatchAuthExpired(message)
          break
        }

        const isLastAttempt = attempt === retryCount

        if (!isLastAttempt) {
          await new Promise((resolve) => setTimeout(resolve, retryDelay))
        }
      }
    }

    // Throw only once after all retries have failed
    throw lastError
  }
}

export default GraphQLClient
